import { fetchApi } from '../client';

/** One automatic-review finding (medopd-docs 143, M2): a code and severity, never the user's text. */
export interface FindingView {
  code: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  detail?: string | null;
  /** DETERMINISTIC (rules) or GENERAL_LLM (AI hint on de-identified text, medopd-docs 148). */
  stage?: string;
  basis?: 'STATED_IN_TEXT' | 'INFERRED';
}

export interface ItemFindings {
  state: 'NONE' | 'RECEIVED' | 'PROCESSING' | 'DONE' | 'SUPERSEDED' | 'PROCESSING_FAILED';
  findings: FindingView[];
  /** Approving needs an override reason (medopd-docs 146). Absent from older servers. */
  requireOverride?: boolean;
  /** What the active policy decided (ESCALATE, HOLD_FOR_REVIEW, ...); null until the check finishes. */
  policyAction?: string | null;
  policyQueue?: string | null;
}

/** Visitor reports of public content, grouped by item (medopd-docs 147). */
export interface ReportedItem {
  type: string;
  contentId: string;
  /** The public text (it is, or was, on the public site), shortened. */
  preview: string;
  /** Still published (false once taken down or changed elsewhere). */
  live: boolean;
  /** Hidden from the public site until a moderator decides. */
  quarantined: boolean;
  reports: number;
  distinctReporters: number;
  reasons: { reason: string; count: number }[];
  /** Visitors' optional notes, newest first. */
  notes: string[];
  firstReportedAt: string;
  lastReportedAt: string;
}

/** A versioned moderation policy (medopd-docs 146). */
export interface ModerationPolicyView {
  id: string;
  version: number;
  status: 'DRAFT' | 'APPROVED' | 'ACTIVE' | 'RETIRED';
  /** The policy JSON as stored. */
  document: string;
  note: string | null;
  authorId: string;
  approverId: string | null;
  /** Names, once the server sends them (it sends ids only today — follow-up #23). */
  authorName?: string | null;
  approverName?: string | null;
  createdAt: string;
  approvedAt: string | null;
  activatedAt: string | null;
}

export interface PolicySimulationRow {
  contentType: string;
  action: string;
  queue: string;
  requireOverride: boolean;
  count: number;
}

export interface PolicySimulation {
  candidateVersion: number;
  activeVersion: number;
  jobsReplayed: number;
  changed: number;
  candidate: PolicySimulationRow[];
  active: PolicySimulationRow[];
}

/** Historical audit of published content (medopd-docs 145). */
export interface AuditTypeProgress {
  type: string;
  published: number;
  queued: number;
  done: number;
  failed: number;
  notYetQueued: number;
}

export interface AuditRunResult {
  dryRun: boolean;
  queuedNow: number;
  progress: AuditTypeProgress[];
}

export interface AuditFlaggedItem {
  type: string;
  contentId: string;
  /** The public text (already on the public site), shortened. */
  preview: string;
  maxSeverity: FindingView['severity'];
  findings: FindingView[];
}

const post = (body: unknown): RequestInit => ({
  method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
});

export const moderationModule = {
  /** @param refs `TYPE:id` strings, at most 100 */
  findings: (refs: string[]) =>
    fetchApi<Record<string, ItemFindings>>(
      `/api/admin/moderation/findings?${refs.map((r) => `ref=${encodeURIComponent(r)}`).join('&')}`),
  reports: {
    open: () => fetchApi<ReportedItem[]>('/api/admin/moderation/reports'),
    keep: (type: string, id: string, note?: string) =>
      fetchApi<void>(`/api/admin/moderation/reports/${encodeURIComponent(type)}/${encodeURIComponent(id)}/keep`, post({ note })),
    takeDown: (type: string, id: string, note: string, reason?: string) =>
      fetchApi<void>(`/api/admin/moderation/reports/${encodeURIComponent(type)}/${encodeURIComponent(id)}/take-down`,
        post({ note, reason })),
  },
  policies: {
    list: () => fetchApi<ModerationPolicyView[]>('/api/admin/moderation/policies'),
    draft: (document: string, note: string) =>
      fetchApi<ModerationPolicyView>('/api/admin/moderation/policies', post({ document, note })),
    approve: (id: string) => fetchApi<ModerationPolicyView>(`/api/admin/moderation/policies/${encodeURIComponent(id)}/approve`, post({})),
    activate: (id: string) => fetchApi<ModerationPolicyView>(`/api/admin/moderation/policies/${encodeURIComponent(id)}/activate`, post({})),
    simulate: (id: string, days: number) =>
      fetchApi<PolicySimulation>(`/api/admin/moderation/policies/${encodeURIComponent(id)}/simulate?days=${days}`),
  },
  audit: {
    progress: () => fetchApi<AuditTypeProgress[]>('/api/admin/moderation/audit'),
    run: (max: number, dryRun: boolean) => fetchApi<AuditRunResult>('/api/admin/moderation/audit/run', post({ max, dryRun })),
    flagged: () => fetchApi<AuditFlaggedItem[]>('/api/admin/moderation/audit/flagged'),
    takeDown: (type: string, id: string, note: string, reason?: string) =>
      fetchApi<void>(`/api/admin/moderation/audit/${encodeURIComponent(type)}/${encodeURIComponent(id)}/take-down`,
        post({ note, reason })),
  },
};
